import { Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';

/** What the field hands its picker: open state and the text typed in the inline search box. */
export interface PickerFieldPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery: string;
}

export interface PickerFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  /** Renders one selected id; resolves its own label. */
  renderChip: (id: string) => ReactNode;
  /** The shared resource picker, opened by Browse or by typing in the search box. */
  renderPicker: (props: PickerFieldPickerProps) => ReactNode;
  searchPlaceholder: string;
  browseLabel: string;
  listLabel: string;
  removeLabel: (id: string) => string;
  id?: string;
  'aria-invalid'?: boolean;
}

/**
 * Shopify's "Applies to" field: chips of the chosen ids, a search box and a Browse button. Typing
 * in the box opens the picker with that text; the picker's result replaces the value.
 */
export function PickerField({
  value,
  onChange,
  onBlur,
  renderChip,
  renderPicker,
  searchPlaceholder,
  browseLabel,
  listLabel,
  removeLabel,
  id,
  'aria-invalid': ariaInvalid,
}: PickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQ('');
      onBlur?.();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            id={id}
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              if (event.target.value.trim()) setOpen(true);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            aria-invalid={ariaInvalid}
            className="pl-8"
          />
        </div>
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          {browseLabel}
        </Button>
      </div>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={listLabel}>
          {value.map((selectedId) => (
            <li key={selectedId}>
              <Badge variant="secondary" className="gap-1 pr-1">
                {renderChip(selectedId)}
                <button
                  type="button"
                  className="hover:bg-foreground/10 rounded-full p-0.5"
                  aria-label={removeLabel(selectedId)}
                  onClick={() => onChange(value.filter((v) => v !== selectedId))}
                >
                  <X className="size-3" aria-hidden />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {renderPicker({ open, onOpenChange, initialQuery: q.trim() })}
    </div>
  );
}
